/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {CreditItem} from "types/entities/PackagesItems";
import clsx from "clsx";
import {currencyNumberFormat} from "utils/utility";
import {pricePerCredits} from "utils/creditsUtils";
import {DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT} from "helpers/config";
import FeatherIcon from "components/FeatherIcon";

interface BookingPackagesCreditsSelectEventsProps {
    sortedPackageCreditItems: CreditItem[];
    getCount: (item: CreditItem) => number;
    handleCountChange: (item: CreditItem, count: number) => void;
    maxTotal: number;
    totalSelected: number;
}

const CreditQuantityButton = (props: {onClick: () => void; disabled: boolean; icon: React.ReactNode}) => {
    return (
        <button
            type={"button"}
            onClick={props.onClick}
            disabled={props.disabled}
            className={clsx(
                "text-black  border rounded-lg flex flex-row justify-center items-center px-2 min-w-[30px] min-h-[30px] font-bold text-lg leading-none",
                props.disabled ? "bg-gray-100 cursor-not-allowed" : "hover:border-arbo-red bg-white"
            )}
        >
            {props.icon}
        </button>
    );
};

/**
 * Events credit package selector.
 * Shows standard-category credit options as rows with a per-row "persons" counter,
 * where the user enters how many people should receive that credit package.
 * The combined count across all rows is capped at the number of persons.
 */
const BookingPackagesCreditsSelectEvents = ({
    sortedPackageCreditItems,
    getCount,
    handleCountChange,
    maxTotal,
    totalSelected,
}: BookingPackagesCreditsSelectEventsProps) => {
    const {t} = useTranslation();

    const renderRow = (item: CreditItem) => {
        const amountTotal = item.article?.creditPackage?.amount_total ?? 0;
        const count = getCount(item);
        const isRecommended = amountTotal === DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT;
        const isActive = count > 0;
        // Remaining capacity for this row: whatever is left of the persons budget plus this row's own count.
        const maxForRow = Math.max(count, maxTotal - (totalSelected - count));

        const setCount = (next: number) => {
            const clamped = Math.min(next, maxForRow);
            if (next < 0) {
                handleCountChange(item, 0);
                return;
            }
            if (clamped === count) {
                return;
            }
            handleCountChange(item, clamped);
        };

        return (
            <div
                key={item.id}
                className={clsx(
                    "BookingPackagesCreditsSelectEvents w-full max-w-full xs:max-w-[300px] sm:max-w-full flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3 xl:gap-4 rounded-xl border-2 py-3 md:py-3 px-3 sm:px-2 md:px-3 xl:px-4 transition-all duration-150",
                    isActive ? "border-arbo-red bg-arbo-red/5" : "border-[#E5E5E5] bg-white"
                )}
            >
                {/* Name + price */}
                <div className={"order-1 flex flex-col md:flex-row items-center text-black  sm:items-start md:items-center gap-0.5 md:gap-3 xl:gap-4 min-w-0"}>
                    <span className={"font-bold text-sm lg:text-base whitespace-nowrap"}>{item.article?.creditPackage?.name ?? t("Unknown")}</span>
                    <div className={"font-medium  flex flex-col leading-4 gap-1 items-center sm:items-start"}>
                        <span className={"text-xs"}>{currencyNumberFormat(item.price)}</span>
                        <span className={"text-xxs"}>
                            {currencyNumberFormat(pricePerCredits(amountTotal))} {t("per credit")}
                        </span>
                    </div>
                </div>

                {/* Top recommendation badge */}
                <div className={clsx("w-full sm:w-auto order-2 mx-auto items-center justify-center", isRecommended ? "flex" : "hidden sm:flex")}>
                    {isRecommended && (
                        <div
                            className={
                                "w-full sm:w-auto xl:whitespace-nowrap flex flex-row lg:flex-col xl:flex-row items-center justify-center gap-1 rounded-xl px-2 sm:px-2.5 lg:px-1.5 xl:px-4 py-1.5 xl:py-2.5 uppercase bg-[linear-gradient(90deg,#FFAE00_0%,#E1570B_35%,#D12C11_70%,#C20016_100%)] text-white"
                            }
                        >
                            <div className={"flex flex-row items-center gap-1.5  font-bold leading-none text-xxs"}>
                                <img src="/img/credits/top-recommendation-icon.svg" alt="top recommendation" className="size-[12px]" />
                                <span>{t("Top")}</span>
                                <span>{t("recommendation")}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="order-3 flex sm:hidden w-full border-t border-dashed border-[#E5E5E5] mt-1 mb-0.5"></div>

                {/* Persons counter */}
                <div className={"order-4 flex flex-row items-center justify-between sm:justify-end gap-2 sm:shrink-0"}>
                    <span className={"font-medium text-xs sm:text-[10px] text-black whitespace-nowrap"}>{t("Persons")}:</span>
                    <div className={"flex flex-row items-center"}>
                        <CreditQuantityButton
                            disabled={count <= 0}
                            icon={<FeatherIcon className={"size-[16px] flex-none"} strokeWidth={4} name={"minus"} />}
                            onClick={() => setCount(count - 1)}
                        />
                        <input
                            className={
                                "text-black bg-transparent text-sm font-bold flex h-full min-h-[30px] min-w-[40px] w-[40px] px-1 text-center placeholder-black appearance-none"
                            }
                            inputMode={"numeric"}
                            type={"text"}
                            value={count}
                            onChange={e => {
                                const parsed = parseInt(e.target.value.replace(/\D/g, ""), 10);
                                setCount(isNaN(parsed) ? 0 : parsed);
                            }}
                        />
                        <CreditQuantityButton
                            disabled={count >= maxForRow}
                            icon={<FeatherIcon className={"size-[16px] flex-none"} strokeWidth={4} name={"plus"} />}
                            onClick={() => setCount(count + 1)}
                        />
                    </div>
                </div>

                <div className="order-5 flex sm:hidden w-full border-t border-dashed border-[#E5E5E5] mt-0.5 mb-0.5"></div>

                {/* Row total */}
                <div
                    className={
                        "order-6 w-full sm:w-[80px] xl:w-[100px] sm:shrink-0 text-right text-black flex flex-row items-center justify-between sm:justify-end"
                    }
                >
                    <span className={"text-xs font-medium inline-flex sm:hidden"}>{t("Total")}</span>
                    <span className={"font-bold text-sm"}>{count === 0 ? "–" : currencyNumberFormat(item.price * count)}</span>
                </div>
            </div>
        );
    };

    return <div className={"BookingPackagesCreditsSelectEvents flex flex-col gap-2 w-full items-center"}>{sortedPackageCreditItems.map(renderRow)}</div>;
};

export default BookingPackagesCreditsSelectEvents;
